// src/components/CustomInput/CustomInput.jsx
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { InputLabel, OutlinedInput, FormHelperText, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CommonStyles } from '../CommonStyles';
import { commonUtility } from '../UtilityFunctions';
import { commonTernary, isWebComponentFlow } from '../../commonCode';
import { COLOR4 } from '../../../constants';
import { emplomentStyles as classes } from '../Employment/EmploymentStyles';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import CommonDataContext from '../../../WebComponents/context/CommonDataContext';

export const CustomInput = ({
  id,
  dataTestId,
  dataTestIdForLabel,
  required = false,
  inputLabelAriaLabel,
  inputLabelSX,
  label,
  autoFocus = false,
  inputSX,
  handleChange,
  placeholder,
  fullWidth = false,
  handleBlur,
  inputProps = {},
  value = '',
  error = '',
  helper = '',
  endAdornment = null,
  startAdornment = null,
  disabled = false,
  onKeyDown,
  multiline = false,
  rows = 0,
  handleFocus,
  isBundle = false,
  optional = false,
  isBundleCompliance = false,
  InputClassName,
  isCDIRA = false,
}) => {
  const baseContext = isWebComponentFlow();
  const { theme, isLightTheme } = baseContext;
  const { t } = useTranslation();
  const styles = CommonStyles(theme);
  const showError = Boolean(error);
  const labelText = label || '';

  const rootSx = [
    styles.outlined_inputs,
    InputClassName || {},
    disabled
      ? isBundle
        ? {
            backgroundColor: `${theme?.palette?.disabledAddress?.main} !important`,
            color: theme?.palette?.disabled?.main,
            WebkitTextFillColor: theme?.palette?.disabled?.main,
          }
        : styles.outlined_inputs_disabled
      : {},
    ...(isBundle
      ? [
          styles.customBorderRadius,
          styles.outlined_input_bundle,
          {
            '&.Mui-focused': {
              border: `1px solid ${theme?.palette?.primary?.main}`,
            },
          },
          {
            '@media (max-width:430px)': {
              '& .MuiOutlinedInput-input': {
                padding: '14px 0px 10px 14px',
              },
            },
          },
        ]
      : [
          {
            '&.Mui-focused': {
              border: `1px solid ${theme?.palette?.primary?.main}`,
            },
          },
        ]),
    isBundleCompliance && classes.select_inputs_bundleBorder,
    showError && isBundle && { backgroundColor: '#D5151F1A !important' },
    isCDIRA && { backgroundColor: '#FFFFFF' },
  ];

  const notchedOutlineSx = [
    error && !isBundleCompliance
      ? styles.notchedOutlineErr
      : error && isBundleCompliance
      ? classes.outline_inputs_err_bundle
      : showError
      ? styles.notchedOutlineErr
      : styles.notchedOutline,
    isBundle && styles.customBorderRadius,
  ];

  return (
    <>
      <InputLabel
        htmlFor={commonUtility(id)}
        data-testid={commonUtility(dataTestIdForLabel)}
        required={required}
        aria-label={commonUtility(inputLabelAriaLabel)}
        sx={[styles.label_title, inputLabelSX]}
      >
        {labelText}{' '}
        {optional && (
          <Box component="span" sx={styles.optionalStyles}>
            {t('Optional', { defaultValue: 'Optional' })}
          </Box>
        )}
      </InputLabel>
      <OutlinedInput
        id={id}
        name={commonUtility(id)}
        data-testid={dataTestId}
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={onKeyDown}
        inputProps={inputProps}
        endAdornment={endAdornment}
        startAdornment={startAdornment}
        autoComplete="off"
        fullWidth={fullWidth}
        sx={rootSx}
        slotProps={{
          root: {
            sx: isBundle ? styles.customBorderRadius : {},
          },
          notchedOutline: {
            sx: notchedOutlineSx,
          },
          input: {
            sx: {
              ...styles.input,
              '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor:
                  isBundle && !isLightTheme ? '#ffffff !important' : 'inherit !important',
                transition: 'background-color 5000s ease-in-out 0s',
              },
              ...(disabled && {
                WebkitTextFillColor: 'unset !important',
              }),
              ...(isBundle && {
                '::-moz-placeholder': {
                  color: theme?.palette?.fieldsPlaceholder?.main,
                  opacity: 1,
                },
                '::placeholder': {
                  color: theme?.palette?.fieldsPlaceholder?.main,
                  opacity: 1,
                  fontSize: '16px',
                },
                '::-webkit-input-placeholder': {
                  color: theme?.palette?.fieldsPlaceholder?.main,
                  opacity: 1,
                },
                '::-ms-input-placeholder': {
                  color: theme?.palette?.fieldsPlaceholder?.main,
                  opacity: 1,
                },
              }),
            },
          },
          adornedEnd: {
            sx: styles.adorned_end,
          },
        }}
      />
    </>
  );
};

CustomInput.propTypes = {
  id: PropTypes.string,
  dataTestId: PropTypes.string,
  dataTestIdForLabel: PropTypes.string,
  required: PropTypes.bool,
  inputLabelAriaLabel: PropTypes.string,
  inputLabelSX: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  label: PropTypes.string,
  autoFocus: PropTypes.bool,
  inputSX: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  handleChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  fullWidth: PropTypes.bool,
  handleBlur: PropTypes.func,
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.string,
  helper: PropTypes.string,
  endAdornment: PropTypes.node,
  startAdornment: PropTypes.node,
  disabled: PropTypes.bool,
  onKeyDown: PropTypes.func,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  handleFocus: PropTypes.func,
  isBundle: PropTypes.bool,
  optional: PropTypes.bool,
  isBundleCompliance: PropTypes.bool,
  InputClassName: PropTypes.object,
  isCDIRA: PropTypes.bool,
};
